import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAccountLookup, useStreemLogin, useGetSSORedirectUrl } from "../lib/api";
import { AccountType } from "../types/auth";
import { encrypt } from "../utils/encryption";
import { useAuthStore } from "../store/useAuthStore";
import { Eye, EyeOff, ArrowRight, GitPullRequestArrow, ChevronLeft } from "lucide-react";
import { toast } from "../lib/use-toast";

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false)
  const [accountType, setAccountType] = useState<AccountType | null>(null);
  const [ssoRedirectUrl, setSsoRedirectUrl] = useState<string | null>(null);
  const [step, setStep] = useState<"username" | "password" | "sso">("username");

  const { mutateAsync: lookupAccount, isPending: isLookingUp } = useAccountLookup();
  const { mutateAsync: login, isPending: isLoggingIn } = useStreemLogin();
  const { mutateAsync: getSSORedirectUrl, isPending: isLoadingSSOUrl } = useGetSSORedirectUrl();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/workflows");
    }
  }, [isAuthenticated, navigate]);

  const handleUsernameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username.trim()) return;

    try {
      const response = await lookupAccount({ username });

      if (response.data) {
        setAccountType(response.data.type);

        if (response.data.type === AccountType.LOCAL || response.data.type === AccountType.DEFAULT_USER) {
          // LOCAL / DEFAULT_USER authentication - show password field
          setStep("password");
        } else {
          // Any non-LOCAL type is treated as SSO (AZURE, OKTA, SAML, etc.)
          // Get SSO redirect URL from backend (uses utility_settings)
          try {
            const state = Math.random().toString(36).substring(7);
            const ssoResponse = await getSSORedirectUrl({ state, username });
            if (ssoResponse.data && ssoResponse.data.redirectUrl) {
              setSsoRedirectUrl(ssoResponse.data.redirectUrl);
              setStep("sso");
            } else {
              throw new Error("Failed to get SSO redirect URL");
            }
          } catch (ssoError) {
            console.error("SSO redirect URL error:", ssoError);
            toast({
              variant: "destructive",
              title: "SSO Setup Error",
              description: "Failed to initialize SSO login. Please try again.",
            });
          }
        }
      } else if (response.errors && response.errors.length > 0) {
        toast({
          variant: "destructive",
          title: "Account Lookup Failed",
          description: response.errors[0].message,
        });
      }
    } catch (error: any) {
      console.error("Account lookup error:", error);

      // Try to extract error message from the response
      const errorMessage =
        error?.response?.data?.errors?.[0]?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to lookup account. Please try again.";

      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password.trim()) return;

    try {
      const encryptedPassword = encrypt(password);
      const response = await login({
        username,
        password: encryptedPassword,
        userType: accountType ?? undefined,
      });

      if (response.data) {
        // Login successful, user will be redirected by auth store
        navigate("/workflows");
      }
    } catch (error: any) {
      console.error("Login error:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.[0]?.message ||
        error?.message ||
        "Invalid credentials. Please try again.";
      toast({
        variant: "destructive",
        title: "Login Failed",
        description: errorMessage,
      });
    }
  };

  const handleSSORedirect = () => {
    if (ssoRedirectUrl) {
      // Store username in auth store for SSO callback page (persisted to localStorage)
      useAuthStore.getState().setPendingUsername(username);
      // Redirect to SSO provider - will callback to /sso/auth
      window.location.href = ssoRedirectUrl;
    }
  };

  const handleBack = () => {
    setStep("username");
    setPassword("");
    setAccountType(null);
    setSsoRedirectUrl(null);
  };

  const isLoading = isLookingUp || isLoggingIn || isLoadingSSOUrl;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        {/* Logo & Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center gap-2 mb-1">
            <GitPullRequestArrow className="w-5 h-5 text-slate-700" />
            <h1 className="text-lg font-semibold text-slate-900">Migration Utility</h1>
          </div>
          <p className="text-sm text-slate-400">Cross-instance data sync</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          {step === "username" && (
            <form onSubmit={handleUsernameSubmit} className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Username or Email
                </label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleUsernameSubmit(e)}
                  placeholder="Write your Username or Email ID here"
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition-all placeholder:text-slate-400"
                  disabled={isLoading}
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={!username.trim() || isLoading}
                className="w-full py-2.5 px-4 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-slate-600 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    Continue
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          )}

          {step === "password" && (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                <button
                  type="button"
                  onClick={handleBack}
                  className="inline-flex items-center gap-0.5 text-sm text-slate-500 hover:text-slate-900 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
                <span className="text-sm text-slate-600">{username}</span>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit(e)}
                    placeholder="Enter your password"
                    className="w-full px-3 py-2.5 pr-10 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition-all placeholder:text-slate-400"
                    disabled={isLoading}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={!password.trim() || isLoading}
                className="w-full py-2.5 px-4 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-slate-600 border-t-white rounded-full animate-spin" />
                ) : (
                  'Sign in'
                )}
              </button>
            </form>
          )}

          {step === "sso" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                <button
                  type="button"
                  onClick={handleBack}
                  className="inline-flex items-center gap-0.5 text-sm text-slate-500 hover:text-slate-900 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
                <span className="text-sm text-slate-600">{username}</span>
              </div>

              <div className="text-center space-y-2">
                <p className="text-sm text-slate-500">
                  You will be redirected to your organization's SSO provider to sign in.
                </p>
              </div>

              <button
                onClick={handleSSORedirect}
                disabled={!ssoRedirectUrl}
                className="w-full py-2.5 px-4 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continue to SSO
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

      </div>

      {/* Footer - Fixed Bottom Right */}
      <div className="fixed bottom-6 right-6">
        <div className="text-xs text-slate-500 mb-2 text-right">A Product By
        <a href="https://www.leucinetech.com" target="_blank" rel="noopener noreferrer">
          <img
            src="data:image/svg+xml;base64,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"
            width="110"
            alt="Leucine"
          />
        </a>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
